import { useState } from 'react'
import { useController } from 'react-hook-form'

import { useAlertsStore } from '@stores/alertsStore'
import useMutation from '@hooks/useGQLMutation'
import useFormWithErrors from '@hooks/useFormWithErrors'

import { Button } from '@atoms'
import FederalDistrictSelect from '@components/molecules/FederalDistrictSelect'
import { AppAlerts } from '@components/molecules/Alerts/AppAlerts'

import { scopedTranslation } from '@utils/I18n'
import type { SelectOption } from '@utils/types'

import { UpdateSupporterDocument } from '@frontend/graphql/mutations/updateSupporter.generated'

const tSupporterShow = scopedTranslation('supporter_show')
const tShared = scopedTranslation('shared.buttons')

const ALERTS_PATH = 'FederalDistrict' as const

type FormData = {
  supporter: {
    federalDistrict: SelectOption | null
  }
}

const useFederalDistrictForm = (
  supporterId: string,
  currentFederalDistrict: string | null,
  onSuccess: (federalDistrict: string) => void
) => {
  const {
    formState: { errors },
    handleSubmit,
    control,
  } = useFormWithErrors<FormData>({
    mode: 'onTouched',
    defaultValues: {
      supporter: { 
        federalDistrict: currentFederalDistrict 
          ? { value: currentFederalDistrict, label: currentFederalDistrict }
          : null 
      },
    },
  })

  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({
    setAlerts,
    clearAlerts,
  }))

  const { field } = useController({
    name: 'supporter.federalDistrict',
    control
  })

  const { mutate } = useMutation(UpdateSupporterDocument)

  const onSubmit = async (values: FormData) => {
    clearAlerts(ALERTS_PATH)

    const mutationData = { 
      id: supporterId, 
      federalDistrict: values.supporter.federalDistrict?.value || null
    }

    mutate(
      mutationData,
      {
        onSuccess: (response) => {
          if (response.updateSupporter?.success) {
            onSuccess(response.updateSupporter?.supporter?.federalDistrict ?? '')
            clearAlerts(ALERTS_PATH)
          } else if (response.updateSupporter) {
            setAlerts({
              alertsPath: ALERTS_PATH,
              alerts: response.updateSupporter.errors?.map((error: any) => ({
                type: 'error' as const,
                details: {
                  title: error.message
                }
              })) || []
            })
          }
        },
        onError: (_error: any) => {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts: [{
              type: 'error' as const,
              details: {
                title: tSupporterShow('federal_districts.save_error')
              }
            }]
          })
        },
      }
    )
  }

  return {
    field,
    errors,
    handleSubmit: handleSubmit(onSubmit),
  }
}

type FederalDistrictFieldProps = {
  supporterId: string
  currentFederalDistrict: string | null
  onCancel: () => void
  onSuccess: (federalDistrict: string) => void
}

function FederalDistrictField({
  supporterId,
  currentFederalDistrict,
  onCancel,
  onSuccess,
}: FederalDistrictFieldProps) {
  const { field, handleSubmit } = useFederalDistrictForm(supporterId, currentFederalDistrict, onSuccess)

  return (
    <div className="tw:col-span-3">
      <form onSubmit={handleSubmit} className="tw:flex tw:flex-col tw:gap-4">
        <AppAlerts alertsPath={ALERTS_PATH} />
        <FederalDistrictSelect
          field={field}
          hideLabel
          placeholder={tSupporterShow('federal_districts.placeholder')}
        />
        <div className="tw:flex tw:gap-2">
          <Button type="submit" size="sm" aria-label={tSupporterShow('federal_districts.aria_labels.save')}>
            {tShared('save')}
          </Button>
          <Button type="button" rank="secondary" size="sm" onClick={onCancel} aria-label={tSupporterShow('federal_districts.aria_labels.cancel')}>
            {tShared('cancel')}
          </Button>
        </div>
      </form>
    </div>
  )
}

function FederalDistrictDisplay({ federalDistrict }: { federalDistrict: string | null }) {
  if (!federalDistrict) {
    return (
      <div className="tw:text-gray-400">
        {tSupporterShow('federal_districts.empty_state')}
      </div>
    )
  }

  return (
    <div className="tw:text-gray-600">
      {federalDistrict}
    </div>
  )
}

type FederalDistrictFormProps = {
  supporterId: string
  initialFederalDistrict: string | null
  editFederalDistrict: boolean
}

function FederalDistrictForm({ supporterId, initialFederalDistrict, editFederalDistrict }: FederalDistrictFormProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [currentFederalDistrict, setCurrentFederalDistrict] = useState(initialFederalDistrict)

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleSuccess = (newFederalDistrict: string) => {
    setCurrentFederalDistrict(newFederalDistrict)
    setIsEditing(false)
  }

  return (
    <section
      data-testid="federal-districts-form"
      className="tw:flex tw:flex-col tw:px-8 tw:shadow-xs tw:rounded-xl tw:border tw:border-gray-200"
      data-compid="supporter-show_federal-districts-form"
    >
      <div className="tw:grid tw:grid-cols-5 tw:grid-rows-1 tw:gap-4 tw:items-center tw:py-5">
        <div>
          <p className="tw:font-medium">{tSupporterShow('federal_districts.title')}</p>
        </div>
        {isEditing ? (
          <FederalDistrictField
            supporterId={supporterId}
            currentFederalDistrict={currentFederalDistrict}
            onCancel={handleCancel}
            onSuccess={handleSuccess}
          />
        ) : (
          <div className="tw:col-span-3">
            <FederalDistrictDisplay federalDistrict={currentFederalDistrict} />
          </div>
        )}

        <div className="tw:col-start-5 tw:flex tw:justify-end">
          {editFederalDistrict && (
            <Button
              type="button"
              rank="secondary"
              onClick={handleEdit}
              leadingIcon="pencil-02"
              disabled={isEditing}
              aria-label={tSupporterShow('federal_districts.aria_labels.edit')}
            >
              {tShared('edit')}
            </Button>
          )}
        </div>
      </div>
    </section>
  )
}

export { FederalDistrictForm }